import { classNames } from '@fullstory/util/fmt';
import React, { Children, createContext, CSSProperties, useContext } from 'react';
import { identifyComponent } from '../../util';
import { Flex } from '../Flex';
import css from './index.module.scss';

export const InputGroupContext = createContext<string>('');
/**
 * This is a hack to support some old legacy styles for button groups
 */
export const InputGroupLegacyContext = createContext<{
  resetRightBorderRadius: boolean;
  resetLeftBorderRadius: boolean;
  textInputWrapperClassName: string;
}>({
  resetLeftBorderRadius: false,
  resetRightBorderRadius: false,
  textInputWrapperClassName: css.legacyTextInputWrapper,
});

export const useInputGroupClassName = () => useContext(InputGroupContext);

export interface InputGroupProps {
  children: React.ReactNode;
  style?: CSSProperties;
}

export const InputGroup = React.forwardRef<HTMLDivElement, InputGroupProps>(
  ({ children, style, ...restOfProps }, forwardedRef) => {
    /**
     * Filter out null children.
     *
     * This handles conditional children cases.
     * ```
     * <InputGroup>
     *   {showButton && <Button />} // is `null` in the `children` array
     *   <Button />
     * </InputGroup>
     * ```
     */
    const childrenArray = Children.toArray(children).filter(Boolean);
    if (childrenArray.length <= 1) return <>{children}</>;

    return (
      <Flex
        data-component="InputGroup"
        {...restOfProps}
        {...identifyComponent('input-group', restOfProps)}
        justifyContent="flex-start"
        alignItems="stretch"
        className={css.group}
        style={style}
        ref={forwardedRef}
      >
        {Children.map(childrenArray, (child, index) => {
          const resetRightBorderRadius = index !== childrenArray.length - 1;
          const resetLeftBorderRadius = index !== 0;
          return (
            <InputGroupContext.Provider
              value={classNames({
                [css.resetRightBorderRadius]: resetRightBorderRadius,
                [css.resetLeftBorderRadius]: resetLeftBorderRadius,
              })}
            >
              <InputGroupLegacyContext.Provider
                value={{
                  resetRightBorderRadius,
                  resetLeftBorderRadius,
                  textInputWrapperClassName: css.legacyTextInputWrapper,
                }}
              >
                {child}
              </InputGroupLegacyContext.Provider>
            </InputGroupContext.Provider>
          );
        })}
      </Flex>
    );
  },
);

InputGroup.displayName = 'InputGroup';
